import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { ICustomisationOption } from '@models/IProductCategoriesAndOptions';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

interface Props {
  readonly categoryType: string;
  readonly dataTestId?: string;
  readonly hasSingleCustomisationCategory: boolean;
  readonly options: ICustomisationOption[];
  readonly selectedOption?: ICustomisationOption;
  readonly onOptionClick: (optionId: string, optionName: string) => void;
}

const ColourOptionsList = ({
  categoryType,
  dataTestId,
  hasSingleCustomisationCategory,
  options,
  selectedOption,
  onOptionClick,
}: Props) => (
  <div
    className="flex flex-wrap gap-[15px]"
    data-testid={`${dataTestId}-Colour-Options`}
  >
    {options.map(
      ({ name, thumbnail, optionId, colour, materialCode }, index) => {
        const isSelectedOption = hasSingleCustomisationCategory
          ? selectedOption?.materialCode === materialCode
          : selectedOption?.optionId === optionId;
        const isWhiteColour = colour === '#FFF' || colour === '#FFFFFF';

        return (
          <motion.button
            key={hasSingleCustomisationCategory ? materialCode : optionId}
            animate={{
              borderWidth: isSelectedOption ? 1 : 0,
              padding: isSelectedOption ? 4 : 0,
            }}
            transition={{
              duration: 0.15,
            }}
            className={classNames(
              'relative flex h-[35px] w-[35px] rounded-full transition-colors',
              {
                'pointer-events-none border-yellow': isSelectedOption,
              }
            )}
            onClick={() => {
              updateDataLayer({
                event: 'productModification',
                content_type: categoryType,
                selection: name,
              });

              onOptionClick(
                hasSingleCustomisationCategory ? materialCode : optionId,
                name
              );
            }}
            data-testid={`${dataTestId}-Option-${index}`}
            aria-label={name}
          >
            {thumbnail && (
              <Image
                image={thumbnail}
                classes="h-full w-full"
                imageClasses="h-full w-full rounded-full"
                dataTestId={`${dataTestId}-Option-${index}-Thumbnail`}
              />
            )}

            {!thumbnail && colour && (
              <span
                className={classNames('h-full w-full rounded-full', {
                  'border border-black': isWhiteColour,
                })}
                style={{ backgroundColor: colour }}
                data-testid={`${dataTestId}-Option-${index}-Colour`}
              />
            )}

            <AnimatePresence>
              {isSelectedOption && (
                <motion.div
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  className={classNames(
                    '-top-0.5 absolute right-0 flex h-[14px] w-[14px] items-center justify-center rounded-full border border-white',
                    isSelectedOption ? 'bg-yellow' : 'bg-grey-400'
                  )}
                  data-testid={`${dataTestId}-Option-${index}-Icon`}
                >
                  <Icon
                    name={isSelectedOption ? 'check' : 'close'}
                    colorClassName="text-black-100 p-px"
                    size={isSelectedOption ? '12px' : '10px'}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        );
      }
    )}
  </div>
);

export default ColourOptionsList;
